import React from "react";
import { Alert, Box, Card, Chip, Typography } from "@mui/material";
import { ReportData } from "../types";
import { COLOR } from "../utils/format";
import { reconciliationRows } from "../utils/reportSections";

const STATUS_COLOR = { reconciled: "success", needs_review: "error", not_entered: "default", monthly_only: "default" } as const;

/**
 * Calculated vs Actual balance — both from the backend; nothing is adjusted.
 * Shown for every period; actual balances only exist per month.
 */
const ReconciliationCard: React.FC<{ report: ReportData }> = ({ report }) => {
  const rec = reconciliationRows(report);
  const difference = report.monthly?.reconciliation.difference ?? null;

  const line = (label: string, value: string, color?: string) => (
    <Box display="flex" justifyContent="space-between" py={0.75} sx={{ borderBottom: "1px solid #f0f0f0" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Card elevation={2} sx={{ borderRadius: 2, p: 2, height: "100%" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          Reconciliation
        </Typography>
        <Chip size="small" label={rec.status} color={STATUS_COLOR[rec.statusKey]} />
      </Box>
      {line("Calculated Balance", rec.calculated)}
      {line("Actual Balance", rec.actual)}
      {line("Difference", rec.difference, difference ? COLOR.redDark : difference === 0 ? COLOR.greenDark : undefined)}
      {rec.note &&
        (rec.statusKey === "needs_review" ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {rec.note}
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {rec.note}
          </Typography>
        ))}
    </Card>
  );
};

export default ReconciliationCard;
